/**
 * How each kind of credential is drawn.
 *
 * Built as a `Record<VaultKind, ...>` over the exported list rather than as a
 * loose object, so TypeScript refuses to compile the day somebody adds a kind on
 * the server and mirrors it into `VAULT_KINDS` without giving it an icon. The
 * alternative - a lookup with a fallback - is how a new kind ends up drawn as
 * whatever the last branch happened to say, and nobody notices for months.
 *
 * The label itself is an i18n key built off the same string (`vault.kinds.<k>`),
 * so there is no third list to keep in step.
 */
import { Database, Globe, KeyRound, LayoutTemplate, Mail, Server, Upload, type LucideIcon } from "lucide-react";
import { VAULT_KINDS, type VaultKind } from "../../../lib/vault";

export const KIND_ICON: Record<VaultKind, LucideIcon> = {
  ftp: Upload,
  mysql: Database,
  cms: LayoutTemplate,
  hosting: Server,
  domain: Globe,
  mail: Mail,
  other: KeyRound,
};

/** The icon for a kind string that came off the wire, which may be anything. */
export function iconFor(kind: string): LucideIcon {
  return (KIND_ICON as Record<string, LucideIcon>)[kind] ?? KeyRound;
}

/** The i18n key for a kind. Unknown kinds fall back to the word "other" rather
 *  than printing a raw enum value into the interface. */
export function kindLabelKey(kind: string): string {
  return (VAULT_KINDS as readonly string[]).includes(kind) ? `vault.kinds.${kind}` : "vault.kinds.other";
}
